import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Grid3X3 } from "lucide-react";
import { EmptyState } from "../../components/ui";
import { ALL_BOOKING_TYPES } from "./catalog/queries";
import { STATE_OPTIONS, type BookingTypeRecord } from "./catalog/types";
import type { Agreement } from "./AgreementCard";
import {
  computeCoverage,
  type CellStatus,
  type CoverageCell,
  type CoverageResult,
} from "./coverage";
import {
  ALL_AGREEMENT_TYPES,
  type AgreementTypeRecord,
} from "../../apollo/agreementTypes";

/* ── Query shape (mirrors catalog listing) ───────────────────────── */

interface AllBookingTypesData {
  allBookingTypes: BookingTypeRecord[];
}

/* ── Cell rendering ──────────────────────────────────────────────── */

const CELL_CONFIG: Record<
  CellStatus,
  { label: string; className: string }
> = {
  covered: {
    label: "Covered",
    className: "text-sage font-medium",
  },
  partial: {
    label: "Partial",
    className: "text-spark font-medium",
  },
  gap: {
    label: "Gap",
    className: "text-rust font-medium",
  },
  na: {
    label: "--",
    className: "text-ink-5",
  },
};

function CellBadge({ cell }: { cell: CoverageCell }) {
  const cfg = CELL_CONFIG[cell.status];
  return (
    <span
      className={`text-[11.5px] ${cfg.className}`}
      title={cell.resolvingAgreement ?? undefined}
    >
      {cfg.label}
    </span>
  );
}

/* ── Legend ───────────────────────────────────────────────────────── */

function Legend() {
  return (
    <div className="flex items-center gap-4 text-[11px] text-ink-4">
      <span className="flex items-center gap-1.5">
        <span className="dot bg-sage" />
        <span>Covered</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="dot bg-spark" />
        <span>Partial (sub-state only)</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="dot bg-rust" />
        <span>Gap (no resolving agreement)</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="dot bg-ink-5" />
        <span>Not offered</span>
      </span>
    </div>
  );
}

/* ── Summary line ────────────────────────────────────────────────── */

function Summary({ result }: { result: CoverageResult }) {
  if (result.gapCount === 0) {
    return (
      <p className="text-[13px] text-sage font-medium">
        No gaps &mdash; every offered service has a resolving agreement.
      </p>
    );
  }
  return (
    <p className="text-[13px] text-rust font-medium">
      {result.gapCount} coverage gap{result.gapCount === 1 ? "" : "s"} across{" "}
      {result.servicesWithGaps} service{result.servicesWithGaps === 1 ? "" : "s"}
    </p>
  );
}

/* ── Main component ──────────────────────────────────────────────── */

interface CoverageMatrixProps {
  agreements: readonly Agreement[];
  agreementsLoading: boolean;
}

export function CoverageMatrix({
  agreements,
  agreementsLoading,
}: CoverageMatrixProps) {
  const btQuery = useQuery<AllBookingTypesData>(ALL_BOOKING_TYPES, {
    fetchPolicy: "cache-and-network",
  });
  const atQuery = useQuery<{ allAgreementTypes: AgreementTypeRecord[] }>(
    ALL_AGREEMENT_TYPES,
    {
      variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" },
      fetchPolicy: "cache-and-network",
    },
  );

  const services = btQuery.data?.allBookingTypes ?? [];
  const agreementTypes = atQuery.data?.allAgreementTypes ?? [];
  const initialLoad =
    (btQuery.loading && !btQuery.data) || agreementsLoading;

  const generalStates = useMemo(
    () => STATE_OPTIONS.filter((o) => o.value).map((o) => o.value),
    [],
  );

  const typeIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of agreementTypes) {
      map.set(t.id, t.name);
    }
    return map;
  }, [agreementTypes]);

  const typeKeyToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of agreementTypes) {
      map.set(t.typeKey, t.id);
    }
    return map;
  }, [agreementTypes]);

  const result = useMemo(
    () =>
      computeCoverage(
        services,
        agreements,
        generalStates,
        typeIdToName,
        typeKeyToId,
      ),
    [services, agreements, generalStates, typeIdToName, typeKeyToId],
  );

  if (initialLoad) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card p-6 space-y-3">
        <div className="h-3 bg-canvas-2 rounded w-48 pulse-soft" />
        <div className="h-3 bg-canvas-2 rounded w-64 pulse-soft" />
        <div className="h-40 bg-canvas-2 rounded pulse-soft" />
      </div>
    );
  }

  if (result.rows.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card">
        <EmptyState
          icon={<Grid3X3 size={28} strokeWidth={1.5} />}
          heading="No active services"
          description="Add services in the catalog to see coverage here."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Intro */}
      <div className="space-y-2">
        <p className="text-[13px] text-ink-3 max-w-xl">
          Does every place each service is offered have an agreement that will
          resolve? Hover a cell to see the resolving agreement.
        </p>
        <Summary result={result} />
        <Legend />
      </div>

      {/* Table */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[480px]">
          <thead>
            <tr className="bg-wash">
              <th className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5 text-left sticky left-0 bg-wash z-10">
                Service
              </th>
              {result.columns.map((col) => (
                <th
                  key={col}
                  className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5 text-center"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-hair">
            {result.rows.map((row) => {
              const hasGap = Object.values(row.cells).some(
                (c) => c.status === "gap",
              );
              return (
                <tr
                  key={row.serviceId}
                  className={hasGap ? "bg-rust/[0.03]" : ""}
                >
                  <td className="px-4 py-2.5 sticky left-0 bg-inherit z-10">
                    <div className="text-[13px] font-medium leading-tight">
                      {row.serviceName}
                    </div>
                    {row.documentLabel ? (
                      <div className="text-[11px] text-ink-4 leading-tight mt-0.5">
                        {row.documentLabel}
                      </div>
                    ) : (
                      <div className="text-[11px] text-rust leading-tight mt-0.5">
                        No document bound
                      </div>
                    )}
                  </td>
                  {result.columns.map((col) => (
                    <td key={col} className="px-4 py-2.5 text-center">
                      <CellBadge cell={row.cells[col]} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
